import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { UserPlus, Mail, Eye, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { employeeAPI } from '../lib/publicAPI';
import { formatAUDate } from '../lib/dateUtils';

export default function Employees() {
  const [employees, setEmployees] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [employeeEmail, setEmployeeEmail] = useState('');
  const [employeeName, setEmployeeName] = useState('');
  const [sending, setSending] = useState(false);
  const [clientId, setClientId] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Get user's client_id from membership
      const meResponse = await api.get('/api/me');
      const membership = meResponse.data.active_membership;
      
      if (membership?.client_id) {
        setClientId(membership.client_id);
        
        // Load employees and invites
        const [employeesRes, invitesRes] = await Promise.all([
          api.get('/api/employees'),
          api.get('/api/employees/invites')
        ]);
        
        setEmployees(employeesRes.data);
        setInvites(invitesRes.data);
      }
    } catch (error) {
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvite = async (e) => {
    e.preventDefault();
    
    if (!employeeEmail || !clientId) {
      toast.error('Please enter employee email');
      return;
    }

    setSending(true);
    try {
      await employeeAPI.sendInvite(clientId, {
        employee_email: employeeEmail,
        employee_name: employeeName || null
      });
      
      toast.success(`Invite sent to ${employeeEmail}`);
      setDialogOpen(false);
      setEmployeeEmail('');
      setEmployeeName('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invite');
    } finally {
      setSending(false);
    }
  };

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    completed: 'bg-green-100 text-green-800 border-green-200',
    expired: 'bg-muted text-muted-foreground border-border'
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/4"></div>
          <div className="h-64 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>
            My Employees
          </h1>
          <p className="text-muted-foreground mt-1">Invite and manage your employees</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90" data-testid="invite-employee-btn">
              <UserPlus className="w-4 h-4 mr-2" />
              Invite Employee
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Invite Employee to Complete Onboarding</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSendInvite} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="emp_name">Employee Name (optional)</Label>
                <Input
                  id="emp_name"
                  placeholder="John Smith"
                  value={employeeName}
                  onChange={(e) => setEmployeeName(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="emp_email">Employee Email *</Label>
                <Input
                  id="emp_email"
                  type="email"
                  placeholder="employee@example.com"
                  value={employeeEmail}
                  onChange={(e) => setEmployeeEmail(e.target.value)}
                  required
                />
              </div>

              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-start gap-2">
                  <Mail className="w-4 h-4 text-blue-600 mt-0.5" />
                  <p className="text-sm text-blue-800">
                    An email will be sent with a secure link for the employee to complete their onboarding form. The link expires in 7 days.
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-primary hover:bg-primary/90" disabled={sending}>
                  {sending ? 'Sending...' : 'Send Invite'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Pending Invites */}
      {invites.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>
              Pending Invitations ({invites.filter(i => i.status === 'pending').length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {invites.filter(i => i.status === 'pending').map((invite) => (
                <div key={invite.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <p className="font-medium text-foreground">{invite.employee_name || invite.employee_email}</p>
                    <p className="text-sm text-muted-foreground">{invite.employee_email}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Sent {formatAUDate(invite.created_at)} • Expires {formatAUDate(invite.expires_at)}
                    </p>
                  </div>
                  <Badge className={statusColors[invite.status]}>
                    {invite.status === 'pending' ? 'Awaiting Response' : invite.status.charAt(0).toUpperCase() + invite.status.slice(1)}
                  </Badge>
                </div>
              ))}
              {invites.filter(i => i.status === 'pending').length === 0 && (
                <p className="text-center py-4 text-muted-foreground">No pending invitations</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Employees List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>Employees ({employees.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {employees.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <UserPlus className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
              <p>No employees yet</p>
              <p className="text-sm mt-1">Invite your first employee using the button above</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="font-semibold text-foreground/90">Name</TableHead>
                  <TableHead className="font-semibold text-foreground/90">Email</TableHead>
                  <TableHead className="font-semibold text-foreground/90">Phone</TableHead>
                  <TableHead className="font-semibold text-foreground/90">Onboarded</TableHead>
                  <TableHead className="font-semibold text-foreground/90">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {employees.map((employee) => (
                  <TableRow key={employee.id}>
                    <TableCell className="font-medium text-foreground">{employee.name}</TableCell>
                    <TableCell className="text-muted-foreground">{employee.email}</TableCell>
                    <TableCell className="text-muted-foreground">{employee.phone || '—'}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">{formatAUDate(employee.created_at)}</TableCell>
                    <TableCell>
                      <Badge className="bg-green-100 text-green-800 border-green-200">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Active
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
